import * as React from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Plus,
  Pencil,
  Trash2,
} from "lucide-react";

import { cn } from "../../lib/utils";
import { AppLayout } from "../../components/ui-v2/layout/AppLayout";
import { EmptyState } from "../../components/ui-v2/empty-state";
import { Card, CardContent } from "../../components/ui-v2/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui-v2/table";
import { Button, RowIconAction } from "../../components/ui-v2/button";
import { Skeleton } from "../../components/ui-v2/skeleton";
import { FilterPillGroup, FilterPill } from "../../components/ui-v2/filter-pill";
import { StatCard } from "../../components/ui-v2/stat-card";
import { CurrencyValue } from "../../components/ui-v2/value-display";
import { Heading, Text, MonoText } from "../../components/ui-v2/typography";
import { AddExpenseDialog } from "../../components/ui-v2/AddExpenseDialog";
import { EditExpenseDialog } from "../../components/ui-v2/EditExpenseDialog";
import { DeleteExpenseDialog } from "../../components/ui-v2/DeleteExpenseDialog";
import { AddIncomeDialog } from "../../components/ui-v2/AddIncomeDialog";
import { EditIncomeDialog } from "../../components/ui-v2/EditIncomeDialog";
import { DeleteIncomeDialog } from "../../components/ui-v2/DeleteIncomeDialog";
import { API_BASE_URL } from '../../lib/api';

/**
 * V2 Cash Flow Page — Institutional Console redesign (issue #237,
 * DESIGN-SPEC.md §5 "Cash flow/Income").
 *
 * Layout: KPI strip (StatCard row) → entries table (2fr, with an All/
 * Recurring/One-time FilterPillGroup in its header) + by-category
 * breakdown bars (1fr), matching CashFlow.dc.html. Sub-tab routing
 * (Income/Expenses) is unchanged — driven by the existing `/cash-flow/:type`
 * route + PageToolbar (DESIGN-SPEC §3), not duplicated here. Refresh/Add
 * actions moved into AppLayout's toolbar slots (secondaryActions/
 * primaryAction) per §3 "Primary action (one max, right-aligned)".
 *
 * KPI strip note (declared deviation — see session file): the mockup's 4th
 * and 3rd KPI cells ("Yield on portfolio", "One-time YTD") are NOT backed by
 * any existing `/income/summary` or `/expenses/summary` API field (verified
 * via backend/services/incomeService.js — only monthlyTotal, yearlyTotal,
 * recurringCount, oneTimeCount exist). Rather than inventing unbacked
 * numbers, this screen keeps the 4 KPIs the API actually returns (Monthly,
 * Yearly, Recurring streams, One-time entries), restyled as StatCards. No
 * functionality — every value shown pre-redesign is still shown.
 */

/**
 * Category chip tint (§5 CashFlow.dc.html `catStyles` map). Falls back to
 * the neutral "Other" tint for categories not explicitly enumerated in the
 * mockup (categories are free-form/user-extensible on the backend).
 *
 * "Capital gains" uses the `allocation-tint` Tailwind color (bound to
 * `--allocation-tint-text`, console-dark.css/console-light.css) instead of a
 * hardcoded `text-[#b4a8ef]` — round-2 fix, ui-design-expert review finding
 * F1: the hardcoded lavender computed 1.86:1 on the light-theme tint
 * background (fails AA even at the 3:1 large-text carve-out); the new token
 * flips to an AA-passing `#665aa8` in console-light while keeping the
 * mockup's exact `#b4a8ef` in console-dark.
 */
const CATEGORY_BADGE_CLASS = {
  Rental: "bg-accent-tint text-accent-text",
  Dividends: "bg-[var(--color-state-success-bg)] text-positive-tint",
  Interest: "bg-[rgba(217,161,59,0.12)] text-warning-tint",
  "Capital gains": "bg-[rgba(139,125,216,0.14)] text-allocation-tint",
  Other: "bg-[rgba(255,255,255,0.06)] text-text-secondary",
};

function getCategoryBadgeClass(category) {
  return CATEGORY_BADGE_CLASS[category] || CATEGORY_BADGE_CLASS.Other;
}

/**
 * Category breakdown BAR color (§5 "by-category breakdown bars",
 * CashFlow.dc.html `catData` — `Rental`→`#3b9eff`, `Dividends`→`#2bb596`,
 * `Interest`→`#d9a13b`, `Other`→`#41586f`).
 *
 * Round-2 fix, ui-design-expert review finding F4 (declared decision, not a
 * spec violation): an earlier revision drew every bar in flat `--accent`
 * blue. Restored the mockup's per-category colors here because they are NOT
 * arbitrary decoration — they reuse the exact same semantic/chart tokens
 * already assigned to each category's badge chip above
 * (`CATEGORY_BADGE_CLASS`: Rental→accent, Dividends→positive,
 * Interest→warning, Other→chart-neutral-1), so the bar and its row's badge
 * always agree. §1.2 ("One accent... reserved for interaction... semantic
 * green/red only ever mean gain/loss/warning — never decoration") governs
 * blue-as-interaction and red/green-as-gain/loss; it does not prohibit this
 * categorical (non-gain/loss, non-interactive) color coding, which was
 * already present in the badge chips one column over. Flat single-accent
 * bars would have made the badge-vs-bar category colors inconsistent within
 * the SAME row for no spec benefit. "Capital gains"/unmapped categories fall
 * back to `--chart-neutral-1` (the mockup's own "Other" bar color), not
 * `--allocation-tint-text` (a TEXT-contrast token, unsuitable as a solid
 * fill color).
 */
const CATEGORY_BAR_COLOR = {
  Rental: "var(--accent, #3b9eff)",
  Dividends: "var(--positive, #2bb596)",
  Interest: "var(--warning, #d9a13b)",
  Other: "var(--chart-neutral-1, #41586f)",
};

function getCategoryBarColor(category) {
  return CATEGORY_BAR_COLOR[category] || CATEGORY_BAR_COLOR.Other;
}

/**
 * CategoryChip - §4.7/§5 category badge (r4, 10.5px 600 uppercase, tinted).
 */
function CategoryChip({ category }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.04em]",
        getCategoryBadgeClass(category)
      )}
    >
      {category}
    </span>
  );
}

/**
 * CategoryBreakdownPanel — §5 "by-category breakdown bars" (1fr column).
 */
function CategoryBreakdownPanel({ breakdownEntries, total, title }) {
  return (
    <Card>
      <div className="border-b border-hairline px-[18px] py-3">
        <span className="text-[13.5px] font-semibold text-foreground">{title}</span>
      </div>
      <CardContent className="flex flex-col gap-3 px-[18px] py-3.5">
        {breakdownEntries.map(([category, data]) => {
          const share = total > 0 ? (data.monthlyTotal / total) * 100 : 0;
          return (
            <div key={category}>
              <div className="flex items-baseline justify-between text-xs">
                <span className="font-medium text-foreground">
                  {category}{" "}
                  <span className="font-mono text-[10.5px] text-text-faint">×{data.count}</span>
                </span>
                <span className="font-mono text-foreground">
                  <CurrencyValue value={data.monthlyTotal} currency="EUR" size="xs" className="font-medium" />
                  <span className="text-text-faint">/mo</span>
                </span>
              </div>
              <div className="mt-1.5 h-[5px] overflow-hidden rounded-[3px] bg-[rgba(255,255,255,0.05)]">
                <div
                  className="h-full rounded-[3px]"
                  data-bar-color={getCategoryBarColor(category)}
                  style={{ width: `${share}%`, backgroundColor: getCategoryBarColor(category) }}
                />
              </div>
              <div className="mt-1 flex justify-between font-mono text-[10.5px] text-text-faint">
                <span>{share.toFixed(0)}% of monthly</span>
                <span>
                  <CurrencyValue value={data.yearlyTotal} currency="EUR" size="xs" className="font-normal text-text-faint" />
                  /yr
                </span>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

/**
 * CashFlowTable — shared entries table for both Income and Expenses,
 * (§5 "entries table (2fr)").
 */
function CashFlowTable({
  title,
  entries,
  entryIdKey,
  activeTypeFilter,
  onTypeFilterChange,
  dateLabel,
  amountCurrencyKey = "currency",
  onEditClick,
  onDeleteClick,
}) {
  const filteredEntries = React.useMemo(() => {
    if (!activeTypeFilter || activeTypeFilter === "all") return entries;
    if (activeTypeFilter === "recurring") {
      return entries.filter((item) => item.frequency && item.frequency !== "one-time");
    }
    return entries.filter((item) => item.frequency === "one-time");
  }, [entries, activeTypeFilter]);

  return (
    <Card>
      <div className="flex items-center gap-3 border-b border-hairline px-[18px] py-2.5">
        <span className="text-[13.5px] font-semibold text-foreground">{title}</span>
        <span className="font-mono text-[11px] text-text-faint">
          {filteredEntries.length} entries
        </span>
        <FilterPillGroup className="ml-auto">
          <FilterPill active={activeTypeFilter === "all"} onClick={() => onTypeFilterChange("all")}>
            All
          </FilterPill>
          <FilterPill active={activeTypeFilter === "recurring"} onClick={() => onTypeFilterChange("recurring")}>
            Recurring
          </FilterPill>
          <FilterPill active={activeTypeFilter === "one-time"} onClick={() => onTypeFilterChange("one-time")}>
            One-time
          </FilterPill>
        </FilterPillGroup>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Source</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Frequency</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="w-20 text-right" aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEntries.slice(0, 15).map((item) => (
              <TableRow key={item[entryIdKey]}>
                <TableCell>
                  <div className="font-medium text-foreground">{item.name}</div>
                  {item[dateLabel.key] && (
                    <MonoText size="xs" className="mt-px block text-text-faint">
                      {dateLabel.prefix}
                      {item[dateLabel.key]}
                    </MonoText>
                  )}
                </TableCell>
                <TableCell>
                  <CategoryChip category={item.category} />
                </TableCell>
                <TableCell className="text-[11.5px] capitalize text-text-secondary">
                  {item.frequency}
                </TableCell>
                <TableCell className="text-right">
                  <CurrencyValue
                    value={item.amount}
                    currency={item[amountCurrencyKey]}
                    size="sm"
                    className="font-semibold"
                  />
                </TableCell>
                <TableCell className="w-20">
                  <div className="flex items-center justify-end gap-1">
                    <RowIconAction intent="neutral" label="Edit" onClick={() => onEditClick(item)}>
                      <Pencil />
                    </RowIconAction>
                    <RowIconAction intent="negative" label="Delete" onClick={() => onDeleteClick(item)}>
                      <Trash2 />
                    </RowIconAction>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function renderCashFlowErrorCard({ error, title, onRetry }) {
  if (!error) return null;
  return (
    <Card className="border-destructive/30 bg-destructive/10">
      <CardContent className="py-6">
        <div className="text-center">
          <Heading level={4} className="text-destructive">
            {title}
          </Heading>
          <Text variant="secondary" className="mt-2">
            {error}
          </Text>
          <Button variant="outline" className="mt-4" onClick={onRetry}>
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            Try Again
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Income Page Content with Full CRUD
 */
function IncomePage({ registerToolbarActions }) {
  const [income, setIncome] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [typeFilter, setTypeFilter] = React.useState("all");

  // CRUD Dialog States
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedIncome, setSelectedIncome] = React.useState(null);

  const fetchData = React.useCallback(async () => {
    try {
      const [incomeRes, summaryRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/income`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/income/summary`, { withCredentials: true }),
      ]);
      setIncome(incomeRes.data || []);
      setSummary(summaryRes.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching income data:", err);
      setError(err.message);
    }
  }, []);

  React.useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await fetchData();
      setLoading(false);
    };
    loadInitialData();
  }, [fetchData]);

  const handleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  }, [fetchData]);

  const handleAddSuccess = () => fetchData();

  const handleEditClick = (item) => {
    setSelectedIncome(item);
    setEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    fetchData();
    setSelectedIncome(null);
  };

  const handleDeleteClick = (item) => {
    setSelectedIncome(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSuccess = () => {
    fetchData();
    setSelectedIncome(null);
  };

  const breakdownEntries = React.useMemo(() => {
    if (!summary?.breakdown) return [];
    return Object.entries(summary.breakdown).sort(
      (a, b) => b[1].monthlyTotal - a[1].monthlyTotal
    );
  }, [summary]);

  React.useEffect(() => {
    registerToolbarActions({
      onAdd: () => setAddDialogOpen(true),
      onRefresh: handleRefresh,
      isRefreshing,
      addLabel: "Add income",
    });
  }, [registerToolbarActions, handleRefresh, isRefreshing]);

  const errorCard = renderCashFlowErrorCard({
    error,
    title: "Failed to load income data",
    onRetry: handleRefresh,
  });

  // Show informative empty state when no data
  if (!loading && !error && income.length === 0) {
    return (
      <>
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            icon={TrendingUp}
            title="No Income Tracked Yet"
            description="Start tracking your investment income including dividends, interest, rental income, and capital gains."
            primaryAction={{
              label: "Add Income",
              onClick: () => setAddDialogOpen(true),
            }}
          />
        </div>
        <AddIncomeDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} onSuccess={handleAddSuccess} />
      </>
    );
  }

  if (error) {
    return errorCard;
  }

  return (
    <div className="flex flex-col gap-3.5">
      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={`income-kpi-skeleton-${i}`} className="py-3.5 px-4">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="mt-2 h-6 w-20" />
            </Card>
          ))
        ) : (
          <>
            <StatCard
              label="Monthly recurring"
              value={new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(summary?.monthlyTotal || 0)}
              delta="Recurring"
            />
            <StatCard
              label="Yearly projection"
              value={new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(summary?.yearlyTotal || 0)}
              delta="all sources"
            />
            <StatCard
              label="Recurring streams"
              value={String(summary?.recurringCount || 0)}
              delta="active streams"
            />
            <StatCard
              label="One-time entries"
              value={String(summary?.oneTimeCount || 0)}
              delta="entries"
            />
          </>
        )}
      </div>

      {/* Entries table + category breakdown */}
      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-[2fr_1fr]">
        {loading ? (
          <Card>
            <CardContent className="space-y-2 p-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={`income-skeleton-${i}`} className="h-12 w-full" />
              ))}
            </CardContent>
          </Card>
        ) : (
          <CashFlowTable
            title="Income entries"
            entries={income}
            entryIdKey="incomeId"
            activeTypeFilter={typeFilter}
            onTypeFilterChange={setTypeFilter}
            dateLabel={{ key: "date", prefix: "" }}
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
          />
        )}

        {!loading && breakdownEntries.length > 0 && (
          <CategoryBreakdownPanel
            breakdownEntries={breakdownEntries}
            total={summary?.monthlyTotal || 0}
            title="By category"
          />
        )}
      </div>

      {/* CRUD Dialogs */}
      <AddIncomeDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} onSuccess={handleAddSuccess} />
      <EditIncomeDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={handleEditSuccess}
        income={selectedIncome}
      />
      <DeleteIncomeDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSuccess={handleDeleteSuccess}
        income={selectedIncome}
      />
    </div>
  );
}

/**
 * Expenses Page Content with Full CRUD
 */
function ExpensesPage({ registerToolbarActions }) {
  const [expenses, setExpenses] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [typeFilter, setTypeFilter] = React.useState("all");

  // CRUD Dialog States
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedExpense, setSelectedExpense] = React.useState(null);

  const fetchData = React.useCallback(async () => {
    try {
      const [expensesRes, summaryRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/expenses`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/expenses/summary`, { withCredentials: true }),
      ]);
      setExpenses(expensesRes.data || []);
      setSummary(summaryRes.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching expenses data:", err);
      setError(err.message);
    }
  }, []);

  React.useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await fetchData();
      setLoading(false);
    };
    loadInitialData();
  }, [fetchData]);

  const handleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  }, [fetchData]);

  const handleAddSuccess = () => fetchData();

  const handleEditClick = (item) => {
    setSelectedExpense(item);
    setEditDialogOpen(true);
  };

  const handleEditSuccess = () => {
    fetchData();
    setSelectedExpense(null);
  };

  const handleDeleteClick = (item) => {
    setSelectedExpense(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSuccess = () => {
    fetchData();
    setSelectedExpense(null);
  };

  const breakdownEntries = React.useMemo(() => {
    if (!summary?.breakdown) return [];
    return Object.entries(summary.breakdown).sort(
      (a, b) => b[1].monthlyTotal - a[1].monthlyTotal
    );
  }, [summary]);

  React.useEffect(() => {
    registerToolbarActions({
      onAdd: () => setAddDialogOpen(true),
      onRefresh: handleRefresh,
      isRefreshing,
      addLabel: "Add expense",
    });
  }, [registerToolbarActions, handleRefresh, isRefreshing]);

  const errorCard = renderCashFlowErrorCard({
    error,
    title: "Failed to load expense data",
    onRetry: handleRefresh,
  });

  // Show informative empty state when no data
  if (!loading && !error && expenses.length === 0) {
    return (
      <>
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            icon={TrendingDown}
            title="No Expenses Tracked Yet"
            description="Start tracking your investment-related expenses including platform fees, trading commissions, subscriptions, and taxes."
            primaryAction={{
              label: "Add Expense",
              onClick: () => setAddDialogOpen(true),
            }}
          />
        </div>
        <AddExpenseDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} onSuccess={handleAddSuccess} />
      </>
    );
  }

  if (error) {
    return errorCard;
  }

  return (
    <div className="flex flex-col gap-3.5">
      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={`expense-kpi-skeleton-${i}`} className="py-3.5 px-4">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="mt-2 h-6 w-20" />
            </Card>
          ))
        ) : (
          <>
            <StatCard
              label="Monthly expenses"
              value={new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(summary?.monthlyTotal || 0)}
              delta="Recurring"
              valueTone="negative"
            />
            <StatCard
              label="Yearly expenses"
              value={new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(summary?.yearlyTotal || 0)}
              delta="all sources"
            />
            <StatCard
              label="Total expenses"
              value={String(summary?.totalCount || 0)}
              delta="active items"
            />
            <StatCard
              label="Renewals"
              value={String(summary?.upcomingRenewals?.length || 0)}
              delta="coming up"
            />
          </>
        )}
      </div>

      {/* Entries table + category breakdown */}
      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-[2fr_1fr]">
        {loading ? (
          <Card>
            <CardContent className="space-y-2 p-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={`expense-skeleton-${i}`} className="h-12 w-full" />
              ))}
            </CardContent>
          </Card>
        ) : (
          <CashFlowTable
            title="Expense entries"
            entries={expenses}
            entryIdKey="expenseId"
            activeTypeFilter={typeFilter}
            onTypeFilterChange={setTypeFilter}
            dateLabel={{ key: "renewalDate", prefix: "Renews: " }}
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
          />
        )}

        {!loading && breakdownEntries.length > 0 && (
          <CategoryBreakdownPanel
            breakdownEntries={breakdownEntries}
            total={summary?.monthlyTotal || 0}
            title="By category"
          />
        )}
      </div>

      {/* CRUD Dialogs */}
      <AddExpenseDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} onSuccess={handleAddSuccess} />
      <EditExpenseDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={handleEditSuccess}
        expense={selectedExpense}
      />
      <DeleteExpenseDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSuccess={handleDeleteSuccess}
        expense={selectedExpense}
      />
    </div>
  );
}

/**
 * V2 Cash Flow Page
 *
 * Displays cash flow data based on URL parameter:
 * - /cash-flow/income
 * - /cash-flow/expenses
 *
 * Toolbar (Refresh / Add) is composed here via a small registration callback
 * so the AppLayout `primaryAction`/`secondaryActions` props (rendered above
 * the page content, in the 46px PageToolbar) can be driven by state that
 * lives inside IncomePage/ExpensesPage, without lifting all of their state
 * up — same "leave seams, don't restructure data flow" approach used by the
 * other #237 screens (CC4 / #201 deferred).
 */
export function CashFlow() {
  const { type } = useParams();
  const isExpenses = type === "expenses";
  const pageTitle = isExpenses ? "Expenses" : "Income";

  const [toolbarActions, setToolbarActions] = React.useState(null);
  const registerToolbarActions = React.useCallback((actions) => {
    setToolbarActions(actions);
  }, []);

  const secondaryActions = toolbarActions ? (
    <Button
      variant="outline"
      size="sm"
      onClick={toolbarActions.onRefresh}
      disabled={toolbarActions.isRefreshing}
    >
      <RefreshCw
        className={cn("h-3.5 w-3.5", toolbarActions.isRefreshing && "animate-spin")}
        aria-hidden="true"
      />
      Refresh
    </Button>
  ) : null;

  const primaryAction = toolbarActions ? (
    <Button size="sm" onClick={toolbarActions.onAdd}>
      <Plus className="h-3.5 w-3.5" aria-hidden="true" />
      {toolbarActions.addLabel}
    </Button>
  ) : null;

  return (
    <AppLayout title={pageTitle} secondaryActions={secondaryActions} primaryAction={primaryAction}>
      <div className="mx-auto max-w-[1600px]">
        {isExpenses ? (
          <ExpensesPage key="expenses" registerToolbarActions={registerToolbarActions} />
        ) : (
          <IncomePage key="income" registerToolbarActions={registerToolbarActions} />
        )}
      </div>
    </AppLayout>
  );
}

export default CashFlow;
